import {
  MAX_CHAT_SHARE_RECIPIENTS,
  SHARE_USER_SEARCH_LIMIT,
} from "@frontend-shared/constants";
import type {
  IChatShareResponse,
  IUserSearchItem,
  TExpiryPreset,
} from "@frontend-shared/types";
import { EChatShareAccessScope } from "@frontend-shared/types";
import { getUserInitials, presetToIso } from "@frontend-shared/utils";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import { Check, Copy, Link, Loader2, Trash2, Users } from "lucide-react";
import {
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAddChatShareRecipients } from "@/api/chat/useAddChatShareRecipients";
import { useCreateChatShare } from "@/api/chat/useCreateChatShare";
import { useGetChatShare } from "@/api/chat/useGetChatShare";
import { useRemoveChatShareRecipients } from "@/api/chat/useRemoveChatShareRecipients";
import { useRevokeChatShare } from "@/api/chat/useRevokeChatShare";
import { useSearchUsers } from "@/api/user/useSearchUsers";
import { StyledDatePicker } from "@/components/StyledDatePicker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  IShareAccessEntry,
  IShareSelectionLimit,
} from "@/components/user-share";
import { ShareAccessList, ShareInviteInput } from "@/components/user-share";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";

import {
  COPY_FEEDBACK_DURATION_MS,
  SCOPE_LABEL_KEY_BY_SCOPE,
} from "./constants";
import { PublicShareSection } from "./PublicShareSection";
import { buildShareUrl } from "./utils";

interface IShareChatDialogProps {
  open: boolean;
  chatUuid: string;
  onOpenChange: (open: boolean) => void;
}

const ShareChatDialogComponent = ({
  open,
  chatUuid,
  onOpenChange,
}: IShareChatDialogProps): React.ReactElement | null => {
  const { t } = useTranslation("chat");
  const { t: tCommon } = useTranslation("common");
  const { user, isPersonalProject } = useAuth();

  const [expiryPreset, setExpiryPreset] = useState<TExpiryPreset>("never");
  const [customDate, setCustomDate] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  // NOTE: Invite typeahead state (team projects only).
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<IUserSearchItem[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionListRef = useRef<HTMLDivElement>(null);
  const inviteContainerRef = useRef<HTMLDivElement>(null);
  const focusAnchorRef = useRef<HTMLSpanElement>(null);

  // NOTE: Bulk-selection state for the access list.
  const [selectedRecipientUuids, setSelectedRecipientUuids] = useState<
    Set<string>
  >(new Set());

  useEffect(() => {
    if (!open) {
      setExpiryPreset("never");
      setCustomDate("");
      setIsCopied(false);
      setSearchQuery("");
      setSelectedUsers([]);
      setIsDropdownVisible(false);
      setHighlightedIndex(-1);
      setSelectedRecipientUuids(new Set());
    }
  }, [open]);

  const {
    data: existingShare,
    isLoading: isLoadingShare,
    error: shareError,
  } = useGetChatShare(chatUuid, open);
  const { mutate: createShare, isPending: isCreating } =
    useCreateChatShare(chatUuid);
  const { mutate: revokeShare, isPending: isRevoking } =
    useRevokeChatShare(chatUuid);
  const { mutate: addRecipients, isPending: isAddingRecipients } =
    useAddChatShareRecipients(chatUuid);
  const { mutate: removeRecipients, isPending: isRemovingRecipients } =
    useRemoveChatShareRecipients(chatUuid);

  const activeShare: IChatShareResponse | null | undefined = existingShare;
  // NOTE: A legacy/API-created active share may be PROJECT/ORG-scoped; the
  // recipient controls only apply to SPECIFIC_PEOPLE shares (the recipient
  // endpoints reject other scopes), so gate them on this.
  const isActiveSpecificPeople =
    activeShare?.accessScope === EChatShareAccessScope.SPECIFIC_PEOPLE;
  const recipients = useMemo(
    () => activeShare?.recipients ?? [],
    [activeShare?.recipients],
  );

  const isChatNotFound =
    isAxiosError(shareError) && shareError.response?.status === 404;
  const shareUrl = activeShare ? buildShareUrl(activeShare.uuid) : null;

  // NOTE: Members are searched only for the team-project invite picker.
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const searchTerm = open ? debouncedSearchQuery.trim() : "";
  const { data: searchResults, isLoading: isSearchLoading } = useSearchUsers({
    query: searchTerm,
    limit: SHARE_USER_SEARCH_LIMIT,
    enabled: !isPersonalProject && (isDropdownVisible || searchTerm.length > 0),
  });

  // NOTE: Only people who cannot be invited drop out of the list. Members the
  // user has ticked but not yet shared with stay in it (checked), so the list
  // does not shift while a multi-selection is being built (NSAI-5025).
  const excludedUuids = useMemo(() => {
    const set = new Set<string>();
    if (user?.uuid) set.add(user.uuid);
    recipients.forEach((recipient) => set.add(recipient.userUuid));
    return set;
  }, [user?.uuid, recipients]);

  const filteredProjectMembers = useMemo(
    () =>
      searchResults.projectMembers.filter(
        (member) => !excludedUuids.has(member.uuid),
      ),
    [searchResults.projectMembers, excludedUuids],
  );
  const filteredOrgMembers = useMemo(
    () =>
      searchResults.organizationMembers.filter(
        (member) => !excludedUuids.has(member.uuid),
      ),
    [searchResults.organizationMembers, excludedUuids],
  );
  const allSuggestions = useMemo(
    () => [...filteredProjectMembers, ...filteredOrgMembers],
    [filteredProjectMembers, filteredOrgMembers],
  );

  const isDebouncing = searchQuery.trim() !== debouncedSearchQuery.trim();
  const isSearching = isSearchLoading || isDebouncing;
  const hasResults = allSuggestions.length > 0;
  // NOTE: An empty list means one of two different things — the query matched
  // nobody, or everyone it matched is already a recipient and got filtered out.
  // The second case is not a failed search, so it gets its own wording instead
  // of the "no matching people" copy (NSAI-4538).
  const isEveryoneAlreadyAdded =
    !hasResults &&
    searchResults.projectMembers.length +
      searchResults.organizationMembers.length >
      0;
  const highlightedValue =
    highlightedIndex >= 0 && highlightedIndex < allSuggestions.length
      ? allSuggestions[highlightedIndex].email
      : "";

  // NOTE: A new query reshuffles the list, so the keyboard highlight starts over.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  useEffect(() => {
    if (!isDropdownVisible) return undefined;
    const handlePointerDown = (event: PointerEvent): void => {
      if (
        inviteContainerRef.current &&
        !inviteContainerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [isDropdownVisible]);

  useEffect(() => {
    if (isChatNotFound && open) {
      onOpenChange(false);
      toast.error(t("share.generate_not_found_error"));
    }
  }, [isChatNotFound, open, onOpenChange, t]);

  const handleMutationError = useCallback(
    (
      error: unknown,
      fallbackKey:
        | "share.generate_error"
        | "share.revoke_error"
        | "share.recipients_error",
    ): void => {
      const isRateLimited =
        isAxiosError(error) && error.response?.status === 429;
      toast.error(
        isRateLimited
          ? `${t(fallbackKey)}. ${tCommon("too_many_requests")}`
          : t(fallbackKey),
      );
    },
    [t, tCommon],
  );

  // NOTE: Only for the requests that carry recipients. Every uuid in them comes
  // from the member search, so a 400 there means the recipient-array cap and
  // nothing else — worth naming, because the generic toast said nothing about
  // the actual cause (NSAI-4538). The personal-project link and revoke keep the
  // generic handler: their 400s have other causes.
  const handleRecipientMutationError = useCallback(
    (
      error: unknown,
      fallbackKey: "share.generate_error" | "share.recipients_error",
    ): void => {
      if (isAxiosError(error) && error.response?.status === 400) {
        toast.error(
          t("share.recipient_limit_error", { max: MAX_CHAT_SHARE_RECIPIENTS }),
        );
        return;
      }
      handleMutationError(error, fallbackKey);
    },
    [handleMutationError, t],
  );

  // NOTE: Checkbox semantics — ticking adds the member to the pending
  // selection, unticking takes them back out, and the list keeps its query and
  // keyboard highlight so a multi-selection can be built in one pass.
  const handleToggleUser = useCallback((toggledUser: IUserSearchItem) => {
    setSelectedUsers((prev) => {
      if (prev.some((existing) => existing.uuid === toggledUser.uuid)) {
        return prev.filter((existing) => existing.uuid !== toggledUser.uuid);
      }
      // NOTE: The endpoint rejects a request carrying more than
      // MAX_CHAT_SHARE_RECIPIENTS people, so the selection stops growing here
      // rather than letting Share fail server-side (NSAI-4538). The rows that
      // are not ticked are disabled at that point, so this guard only catches
      // the highlight-plus-Enter path.
      if (prev.length >= MAX_CHAT_SHARE_RECIPIENTS) {
        return prev;
      }
      return [...prev, toggledUser];
    });
    inputRef.current?.focus();
  }, []);

  const handleRemoveUser = useCallback((userUuid: string) => {
    setSelectedUsers((prev) => prev.filter((item) => item.uuid !== userUuid));
  }, []);

  const handleClearSelectedUsers = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  const handleOpenDropdown = useCallback(() => setIsDropdownVisible(true), []);

  const handleToggleDropdown = useCallback(() => {
    setIsDropdownVisible((previous) => !previous);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key === "Backspace" &&
        searchQuery.length === 0 &&
        selectedUsers.length > 0
      ) {
        setSelectedUsers((prev) => prev.slice(0, -1));
        return;
      }
      // NOTE: Escape is handled by the dialog's onEscapeKeyDown instead —
      // Radix listens for it on the document in the capture phase, so nothing
      // this handler does could stop the dialog from closing first.
      if (!isDropdownVisible || allSuggestions.length === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((index) =>
          index >= allSuggestions.length - 1 ? 0 : index + 1,
        );
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((index) =>
          index <= 0 ? allSuggestions.length - 1 : index - 1,
        );
        return;
      }
      if (event.key === "Enter" && highlightedIndex >= 0) {
        event.preventDefault();
        handleToggleUser(allSuggestions[highlightedIndex]);
      }
    },
    [
      searchQuery,
      selectedUsers.length,
      isDropdownVisible,
      allSuggestions,
      highlightedIndex,
      handleToggleUser,
    ],
  );

  const resetInviteInput = useCallback(() => {
    setSelectedUsers([]);
    setSearchQuery("");
    setIsDropdownVisible(false);
    setHighlightedIndex(-1);
  }, []);

  const handleInvite = useCallback(() => {
    if (selectedUsers.length === 0) return;
    // NOTE: Unreachable while the picker's cap matches the server's — kept so a
    // drift between the two surfaces as the cap message rather than a rejected
    // request (NSAI-4538).
    if (selectedUsers.length > MAX_CHAT_SHARE_RECIPIENTS) {
      toast.error(
        t("share.recipient_limit_error", { max: MAX_CHAT_SHARE_RECIPIENTS }),
      );
      return;
    }
    const recipientUserUuids = selectedUsers.map((item) => item.uuid);

    if (activeShare) {
      addRecipients(
        { recipientUserUuids },
        {
          onSuccess: resetInviteInput,
          onError: (error) =>
            handleRecipientMutationError(error, "share.recipients_error"),
        },
      );
      return;
    }

    const expiresAt = presetToIso({ preset: expiryPreset, customDate });
    createShare(
      {
        accessScope: EChatShareAccessScope.SPECIFIC_PEOPLE,
        expiresAt,
        recipientUserUuids,
      },
      {
        onSuccess: resetInviteInput,
        onError: (error) =>
          handleRecipientMutationError(error, "share.generate_error"),
      },
    );
  }, [
    selectedUsers,
    activeShare,
    addRecipients,
    createShare,
    expiryPreset,
    customDate,
    resetInviteInput,
    handleRecipientMutationError,
    t,
  ]);

  const handleGeneratePersonalLink = useCallback(() => {
    const expiresAt = presetToIso({ preset: expiryPreset, customDate });
    // NOTE: Personal projects have no members to invite, so they keep the
    // "anyone with the link" model — a project-scoped share with a null org
    // grants access to any authenticated link-holder (see the service's
    // personal-project shortcut).
    createShare(
      { accessScope: EChatShareAccessScope.PROJECT, expiresAt },
      {
        onError: (error) => handleMutationError(error, "share.generate_error"),
      },
    );
  }, [expiryPreset, customDate, createShare, handleMutationError]);

  const handleCopy = useCallback(() => {
    if (!shareUrl) return;
    void navigator.clipboard.writeText(shareUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION_MS);
    });
  }, [shareUrl]);

  const handleRevoke = useCallback(() => {
    revokeShare(undefined, {
      onSuccess: () => {
        toast.success(t("share.revoke_success"));
        onOpenChange(false);
      },
      onError: (error) => handleMutationError(error, "share.revoke_error"),
    });
  }, [revokeShare, handleMutationError, onOpenChange, t]);

  const handleRemoveRecipient = useCallback(
    (recipientUuid: string) => {
      removeRecipients([recipientUuid], {
        onSuccess: (data) => {
          if (data.recipients.length === 0) {
            toast.success(t("share.revoke_success"));
            onOpenChange(false);
          }
        },
        onError: (error) =>
          handleMutationError(error, "share.recipients_error"),
      });
    },
    [removeRecipients, handleMutationError, onOpenChange, t],
  );

  const handleSelectOne = useCallback((id: string, isSelected: boolean) => {
    setSelectedRecipientUuids((prev) => {
      const next = new Set(prev);
      if (isSelected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const selectableRecipientIds = useMemo(
    () => recipients.map((recipient) => recipient.uuid),
    [recipients],
  );

  const handleSelectAll = useCallback(
    () => setSelectedRecipientUuids(new Set(selectableRecipientIds)),
    [selectableRecipientIds],
  );
  const handleDeselectAll = useCallback(
    () => setSelectedRecipientUuids(new Set()),
    [],
  );

  const handleBulkRemove = useCallback(() => {
    const ids = Array.from(selectedRecipientUuids);
    if (ids.length === 0 || isRemovingRecipients) return;
    removeRecipients(ids, {
      onSuccess: (data) => {
        setSelectedRecipientUuids(new Set());
        if (data.recipients.length === 0) {
          toast.success(t("share.revoke_success"));
          onOpenChange(false);
        }
      },
      onError: (error) => handleMutationError(error, "share.recipients_error"),
    });
  }, [
    selectedRecipientUuids,
    isRemovingRecipients,
    removeRecipients,
    handleMutationError,
    onOpenChange,
    t,
  ]);

  const accessEntries = useMemo<IShareAccessEntry[]>(
    () =>
      recipients.map((recipient) => ({
        id: recipient.uuid,
        fullName:
          [recipient.userFirstName, recipient.userLastName]
            .filter(Boolean)
            .join(" ")
            .trim() || null,
        email: recipient.userEmail,
        // NOTE: Always false — this dialog is rendered only for the chat owner,
        // and an owner is never one of their own share recipients. It drives
        // the row's "Leave" vs "Remove" label; the owner always sees "Remove".
        isCurrentUser: false,
      })),
    [recipients],
  );

  const hostName =
    `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
    user?.email ||
    "";
  const hostInitials = getUserInitials({
    firstName: user?.firstName ?? null,
    lastName: user?.lastName ?? null,
  });

  const inviteLabels = useMemo(
    () => ({
      placeholder: t("share.invite_placeholder"),
      invite: t("share.invite_button"),
      clearAll: t("share.clear_all"),
      noResults: isEveryoneAlreadyAdded
        ? t("share.no_results_all_added")
        : t("share.no_results"),
      projectMembersHeading: t("share.group_project_members"),
      organizationMembersHeading: t("share.group_organization_members"),
      unknownUser: t("share.unknown_user"),
      showMembers: t("share.invite_show_members"),
      hideMembers: t("share.invite_hide_members"),
      formatSelectedCount: ({ current }: { current: number }): string =>
        t("share.invite_selected_count", { current }),
      formatSelectMemberAria: ({ name }: { name: string }): string =>
        t("share.invite_select_member", { name }),
      formatUnselectMemberAria: ({ name }: { name: string }): string =>
        t("share.invite_unselect_member", { name }),
    }),
    [t, isEveryoneAlreadyAdded],
  );

  const selectionLimit = useMemo<IShareSelectionLimit>(
    () => ({
      max: MAX_CHAT_SHARE_RECIPIENTS,
      reachedMessage: t("share.recipient_limit_reached", {
        max: MAX_CHAT_SHARE_RECIPIENTS,
      }),
    }),
    [t],
  );

  const accessListLabels = useMemo(
    () => ({
      title: t("share.people_with_access"),
      loading: t("share.loading"),
      hostBadge: t("share.host_badge"),
      bulk: {
        selectAllLabel: t("share.bulk_select_all"),
        removeLabel: t("share.bulk_remove"),
        selectAllAria: t("share.bulk_select_all"),
        deselectAllAria: t("share.bulk_deselect_all"),
        formatSelected: (count: number): string =>
          t("share.bulk_selected", { count }),
      },
      row: {
        permissionBadge: t("share.permission_viewer"),
        removeLabel: t("share.bulk_remove"),
        leaveLabel: t("share.bulk_remove"),
        unknownUser: t("share.unknown_user"),
        formatSelectAria: (name: string): string =>
          t("share.bulk_select_user", { name }),
      },
    }),
    [t],
  );

  const expiryLabel = activeShare?.expiresAt
    ? t("share.expires_on", {
        date: format(new Date(activeShare.expiresAt), "PP", {
          locale: undefined,
        }),
      })
    : t("share.no_expiry");

  // NOTE: renderShareLink renders for ANY active share, including a legacy
  // PROJECT/ORGANIZATION-scoped one created before this feature. Derive the
  // scope label from the actual accessScope so those shares don't get
  // mislabelled as "Specific people".
  const activeScopeLabel = t(
    SCOPE_LABEL_KEY_BY_SCOPE[
      activeShare?.accessScope ?? EChatShareAccessScope.SPECIFIC_PEOPLE
    ],
  );

  const isSharing = isCreating || isAddingRecipients;

  const renderExpirySelector = (): React.ReactElement => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="share-expiry">{t("share.expiry_label")}</Label>
      <Select
        value={expiryPreset}
        onValueChange={(value) => setExpiryPreset(value as TExpiryPreset)}
      >
        <SelectTrigger id="share-expiry" data-testid="share-expiry-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="never">{t("share.expiry_never")}</SelectItem>
          <SelectItem value="1d">{t("share.expiry_1_day")}</SelectItem>
          <SelectItem value="7d">{t("share.expiry_7_days")}</SelectItem>
          <SelectItem value="30d">{t("share.expiry_30_days")}</SelectItem>
          <SelectItem value="custom">{t("share.expiry_custom")}</SelectItem>
        </SelectContent>
      </Select>
      {expiryPreset === "custom" && (
        <StyledDatePicker
          value={customDate}
          onChange={(val) =>
            setCustomDate(
              val ? format(new Date(val as string), "yyyy-MM-dd") : "",
            )
          }
          fromDate={new Date()}
          placeholder={t("share.expiry_date_placeholder")}
          data-testid="share-expiry-custom-date"
          wrapperClassName="mt-1"
        />
      )}
    </div>
  );

  const renderShareLink = (): React.ReactElement | null => {
    if (!shareUrl) return null;
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid="share-scope-label">
            {t("share.active_description", {
              scope: activeScopeLabel,
            })}
          </span>
          <span className="text-xs" data-testid="share-expiry-label">
            {expiryLabel}
          </span>
        </div>
        <div className="flex w-full items-center gap-1 rounded-md border border-border bg-muted/50 pl-3">
          <span
            className="min-w-0 flex-1 truncate select-none font-mono text-xs text-foreground"
            data-testid="share-url-text"
          >
            {shareUrl}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopy}
            aria-label={t("share.copy_button")}
            data-testid="share-copy-btn"
            className="shrink-0"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderRevokeButton = (): React.ReactElement => (
    <div className="flex flex-col gap-1.5">
      <Button
        variant="destructive"
        onClick={handleRevoke}
        disabled={isRevoking}
        data-testid="share-revoke-btn"
        className="w-full"
      >
        {isRevoking ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="mr-2 h-4 w-4" />
        )}
        {t("share.revoke_button")}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        {t("share.revoke_tooltip")}
      </p>
    </div>
  );

  const renderInviteInput = (): React.ReactElement => (
    <ShareInviteInput
      selectedUsers={selectedUsers}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onToggleUser={handleToggleUser}
      onRemoveUser={handleRemoveUser}
      onClearSelectedUsers={handleClearSelectedUsers}
      onInvite={handleInvite}
      onKeyDown={handleKeyDown}
      onOpenDropdown={handleOpenDropdown}
      onToggleDropdown={handleToggleDropdown}
      isSearching={isSearching}
      isSharing={isSharing}
      canInviteCurrent
      isDropdownVisible={isDropdownVisible}
      hasResults={hasResults}
      filteredProjectMembers={filteredProjectMembers}
      filteredOrgMembers={filteredOrgMembers}
      highlightedValue={highlightedValue}
      inputRef={inputRef}
      suggestionListRef={suggestionListRef}
      containerRef={inviteContainerRef}
      // NOTE: Creating the first share applies the chosen expiry, so block
      // sharing until a required custom date is picked (matches the personal
      // project's Generate-link guard). Not applicable once a share exists.
      submitDisabled={
        !activeShare && expiryPreset === "custom" && customDate === ""
      }
      selectionLimit={selectionLimit}
      labels={inviteLabels}
    />
  );

  const renderBody = (): React.ReactElement | null => {
    if (isLoadingShare) {
      return (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (isChatNotFound) return null;

    // NOTE: Personal projects keep the "anyone with the link" model — no picker.
    if (isPersonalProject) {
      if (!activeShare) {
        return (
          <div className="flex w-full flex-col gap-3">
            <p
              className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
              data-testid="share-scope-anyone"
            >
              {t("share.scope_anyone")}
            </p>
            {renderExpirySelector()}
            <Button
              onClick={handleGeneratePersonalLink}
              disabled={
                isCreating || (expiryPreset === "custom" && !customDate)
              }
              data-testid="share-generate-btn"
              className="w-full"
            >
              {isCreating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link className="mr-2 h-4 w-4" />
              )}
              {t("share.generate_button")}
            </Button>
          </div>
        );
      }
      return (
        <div className="flex w-full flex-col gap-3">
          {renderShareLink()}
          {renderRevokeButton()}
        </div>
      );
    }

    // NOTE: Team projects share with specific people only.
    if (!activeShare) {
      return (
        <div className="flex w-full flex-col gap-4">
          {renderInviteInput()}
          {renderExpirySelector()}
        </div>
      );
    }

    return (
      <div className="flex w-full flex-col gap-4">
        {renderShareLink()}
        {/* NOTE: Recipient management applies only to specific-people shares.
            A legacy project/organization-scoped active share (from before this
            feature, or created via the API) shows just the link + revoke —
            adding recipients to it would fail server-side, so revoke-first. */}
        {isActiveSpecificPeople && (
          <>
            {renderInviteInput()}
            <ShareAccessList
              hostName={hostName}
              hostEmail={user?.email ?? ""}
              hostInitials={hostInitials}
              entries={accessEntries}
              canManage
              isLoading={false}
              isRemoving={isRemovingRecipients}
              onRemove={handleRemoveRecipient}
              selectableIds={selectableRecipientIds}
              selectedIds={selectedRecipientUuids}
              isBulkPending={isRemovingRecipients}
              onSelectOne={handleSelectOne}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              onBulkRemove={handleBulkRemove}
              labels={accessListLabels}
            />
          </>
        )}
        {renderRevokeButton()}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100%-2rem)] p-7 sm:max-w-lg"
        data-testid="share-chat-dialog"
        // NOTE: Park initial focus on a neutral anchor instead of letting Radix
        // auto-focus the first field (the invite search input) — otherwise the
        // input focuses on open, which would immediately pop the suggestions
        // dropdown before the user interacts.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          focusAnchorRef.current?.focus();
        }}
        // NOTE: While the members list is expanded, Escape collapses it and
        // leaves the dialog open; a second Escape closes the dialog. Radix
        // watches for the key on the document in the capture phase, so this
        // prop is the only place that can win the race.
        onEscapeKeyDown={(event) => {
          if (!isDropdownVisible) {
            return;
          }
          event.preventDefault();
          setIsDropdownVisible(false);
          setHighlightedIndex(-1);
        }}
      >
        <span ref={focusAnchorRef} tabIndex={-1} className="sr-only" />
        <DialogHeader>
          <DialogTitle data-testid="share-chat-dialog-title">
            {t("share.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex w-full min-w-0 flex-col gap-5 px-0.5 pt-2">
          {/* NOTE: NSAI-6550 — the account-scoped share is a bordered section
              with a heading, like the public-link section below it. The two
              scopes used to look like one flat form with a box at the bottom,
              so the expiry selector — which belongs to THIS section and not to
              the public link — read as if it applied to the dialog as a whole.
              The public link's own "never expires" line was a footnote easily
              missed. Same shell, so which setting governs which audience is
              legible without reading the small print. */}
          <section
            className="rounded-lg border border-border p-4"
            data-testid="chat-member-share-section"
          >
            <div className="flex items-start gap-2 pb-3">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-foreground">
                  {t("share.member_share.title")}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t("share.member_share.subtitle")}
                </p>
              </div>
            </div>
            {renderBody()}
          </section>
          {/* NOTE: NSAI-5823 — the public (no-account) link, kept visually
              separate from the account-scoped shares above because it reaches a
              different audience: anyone holding the link and its password.
              `canManage` is the dialog's own gating — both call sites render it
              only for a chat in the user's own list, behind CHAT_SHARE_WRITE.
              Ownership itself is enforced server-side, which is what actually
              decides whether a link can be minted. */}
          <PublicShareSection chatUuid={chatUuid} canManage />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ShareChatDialog = memo(ShareChatDialogComponent);
